// src/lib/billing/past-due-banner.ts
//
// What (if anything) to tell a customer whose card just failed.
//
// WHY THIS EXISTS. Before 2026-08-10 a failed renewal charge dropped the user
// to FREE the same minute and the app started showing them "Upgrade to Pro" —
// as though they had never paid. A real customer hit exactly that: subscribed
// 2026-07-09, renewal failed 2026-08-09, locked out instantly, inside a period
// billed to 2026-09-09, with no dunning email (none had ever sent) to explain
// it. Access is now preserved for DUNNING_GRACE_DAYS, but preserving access
// silently is only half a fix: they still need to know a card needs attention,
// or the grace window simply expires and they are locked out later instead.
//
// Pure and DB-free so the copy and the countdown can be tested exhaustively.

import { DUNNING_GRACE_DAYS } from './effective-plan'

export type PastDueBannerState = {
  /** Whole days remaining in the grace window (0 = last day). */
  daysLeft: number
  /** When access actually ends if nothing is paid. */
  graceEndsAt: Date
  /** Last day — warrants firmer wording. */
  urgent: boolean
}

export type PastDueInput = {
  subscriptionStatus: string | null
  pastDueSince?: Date | null
  currentPeriodEnd?: Date | null
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * The banner to show, or null for the overwhelming majority of users.
 *
 * Returns null once grace has expired: at that point the user is genuinely on
 * FREE and the ordinary upgrade path is the honest thing to show — a banner
 * promising to "keep your Pro" would be describing access they no longer have.
 */
export function pastDueBannerState(
  user: PastDueInput,
  now: Date = new Date(),
): PastDueBannerState | null {
  if (user.subscriptionStatus !== 'past_due') return null
  if (user.pastDueSince == null) return null

  const nowMs = now.getTime()
  // Mirror getEffectivePlan exactly: BOTH bounds, whichever expires first.
  // If these two ever disagree the banner would promise access the gate does
  // not grant, which is worse than no banner at all.
  const graceEnd = Math.min(
    user.pastDueSince.getTime() + DUNNING_GRACE_DAYS * DAY_MS,
    user.currentPeriodEnd?.getTime() ?? Number.POSITIVE_INFINITY,
  )
  if (!Number.isFinite(graceEnd)) return null // no period end known → no grace
  if (nowMs >= graceEnd) return null

  const daysLeft = Math.floor((graceEnd - nowMs) / DAY_MS)
  return { daysLeft, graceEndsAt: new Date(graceEnd), urgent: daysLeft <= 1 }
}

/**
 * Banner copy. Deliberately blames the card, not the customer, and never uses
 * the word "upgrade" — they are already paying, and being sold something they
 * already bought is the exact insult this whole change exists to remove.
 */
export function pastDueBannerMessage(state: PastDueBannerState): string {
  if (state.daysLeft <= 0) return 'Your payment didn’t go through. Pro access ends today.'
  if (state.daysLeft === 1) return 'Your payment didn’t go through. Pro access ends tomorrow.'
  return `Your payment didn’t go through. Pro access continues for ${state.daysLeft} more days.`
}
